import { useMsal } from "@azure/msal-react";
import { useGoogleLogin } from "@react-oauth/google";
import { useState } from "react";
import config from "../../config/config.json";

export default function LoginSSO({ verifySsoToken, validatingOTP, provider, setProvider }) {
  const [processing, setProcessing] = useState(false);
  const { instance } = useMsal();

  const handleGoogleSuccess = (res) => {
    verifySsoToken(res.access_token, provider);
    setProcessing(false);
  };

  const handleError = (err) => {
    setProcessing(false);
    setProvider("");
    verifySsoToken(null, null, err);
  };

  const handleGoogleLogin = (e) => {
    setProcessing(true);
    setProvider("GOOGLE");
    googleLogin(e);
  };

  const handleMicrosoftLogin = () => {
    setProcessing(true);
    setProvider("MS");
    instance
      .loginPopup()
      .then((res) => {
        verifySsoToken(res.idToken, "MS");
        setProcessing(false);
      })
      .catch((err) => {
        handleError(err);
      });
  };

  const handleSalesforceLogin = () => {
    setProcessing(true);
    setProvider("SALESFORCE");

    // Salesforce OAuth 2.0 User-Agent Flow (implicit)
    const clientId = config.SALESFORCE_OAUTH_CLIENT_ID;
    const redirectUri = encodeURIComponent(config.authWeb + "/callback/salesforce");
    // Use 'openid' and 'id' scopes - 'id' returns user info including email via userinfo endpoint
    // Note: These scopes must be enabled in your Salesforce External Client App settings
    const authUrl = `https://login.salesforce.com/services/oauth2/authorize?response_type=token&client_id=${clientId}&redirect_uri=${redirectUri}&scope=web`;

    // Open popup for Salesforce login
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const popup = window.open(
      authUrl,
      "SalesforceLogin",
      `width=${width},height=${height},left=${left},top=${top}`
    );

    // Listen for the callback
    const checkPopup = setInterval(() => {
      try {
        if (!popup || popup.closed) {
          clearInterval(checkPopup);
          setProcessing(false);
          setProvider("");
          return;
        }

        // Check if we're on the callback URL
        if (popup.location.href.includes("/callback/salesforce")) {
          const hash = popup.location.hash;
          popup.close();
          clearInterval(checkPopup);

          // Parse the access token from the hash
          const params = new URLSearchParams(hash.substring(1));
          const accessToken = params.get("access_token");

          if (accessToken) {
            verifySsoToken(accessToken, "SALESFORCE");
          } else {
            handleError(new Error("No access token received from Salesforce"));
          }
          setProcessing(false);
        }
      } catch (e) {
        // Cross-origin error - popup is on Salesforce domain, keep waiting
      }
    }, 500);
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: handleError,
    flow: "implicit"
  });

  const getGoogleSignInBtn = () => {
    if (config.blockGoogleLogin && (processing || validatingOTP) && provider !== "GOOGLE") {
      return null;
    }

    return (
      <button
        type="button"
        className="login-with-btn google-btn w-full"
        onClick={handleGoogleLogin}
        disabled={validatingOTP}
      >
        Sign in with Google
        {validatingOTP || processing ? (
          <span className="absolute right-0 text-sm mr-3">
            {validatingOTP || processing ? (
              <i className="fa-solid fa-circle-notch fa-spin text-sp-800"></i>
            ) : (
              <i className="fa-solid fa-check text-green-500"></i>
            )}
          </span>
        ) : null}
      </button>
    );
  };

  const getMicrosoftSignInBtn = () => {
    if (config.blockMSLogin && (processing || validatingOTP) && provider !== "MS") {
      return null;
    }

    return (
      <button
        type="button"
        className="mt-4 login-with-btn ms-btn w-full"
        onClick={handleMicrosoftLogin}
        disabled={validatingOTP}
      >
        Sign in with Microsoft
        {validatingOTP || processing ? (
          <span className="absolute right-0 text-sm mr-3">
            {validatingOTP || processing ? (
              <i className="fa-solid fa-circle-notch fa-spin text-sp-800"></i>
            ) : (
              <i className="fa-solid fa-check text-green-500"></i>
            )}
          </span>
        ) : null}
      </button>
    );
  };

  const getSalesforceSignInBtn = () => {
    // Completely hide Salesforce button if blocked in config
    if (config.blockSalesforceLogin) {
      return null;
    }

    return (
      <button
        type="button"
        className="mt-4 login-with-btn salesforce-btn w-full"
        onClick={handleSalesforceLogin}
        disabled={validatingOTP || processing}
      >
        Sign in with Salesforce
        {(validatingOTP || processing) && provider === "SALESFORCE" ? (
          <span className="absolute right-0 text-sm mr-3">
            <i className="fa-solid fa-circle-notch fa-spin text-sp-800"></i>
          </span>
        ) : null}
      </button>
    );
  };

  return (
    <div className="relative w-full py-3">
      {getGoogleSignInBtn()}
      {getMicrosoftSignInBtn()}
      {getSalesforceSignInBtn()}
    </div>
  );
}
